/**
 * The Change card mapping utility's model (KPE-028, KPR-707). Pure: what the
 * page draws and sends is worked out here, so the rules are tested without a
 * DOM.
 *
 * The mapping is per TARGET element — the dry run's `fills` is keyed by
 * target — and each entry names the source element that fills it, with the
 * choice map, unit or currency the pair needs (asked once for the mapping):
 *
 *   mapping = { [targetName]: { source, choices?, unit?, currency? } | { kind: 'compose', text, choices?, unit?, currency? } }
 *   leftBehind = { [sourceName]: 'comment' | 'lose' }
 *
 * A `compose` entry is a value built in the composer (KPR-708): the composer's
 * text, words and pills, which `composerModel.js` turns into the
 * fill. When that text is one plain source pill it carries the pair's choice
 * map, unit and currency like an element entry does.
 *
 * Whether a source may fill a target is the vendored compatibility table's
 * (`utils/changeCard/compat.js`) and nothing else, so the utility never offers
 * a pair the dry run and the move refuse.
 */
import { classify, offeredSources, offeredTargets, suggestChoiceMap } from '../../utils/changeCard/compat';
import { takesParts } from '../../utils/changeCard/compose';
import { composerFill, plainSourceOf, sourcesRead, tokenOf } from './composerModel';
import { cardChain } from '../../utils/cards';
import { defaultCodeOf } from '../../utils/currency';
import { defaultUnitFor } from '../../utils/measurements';
import { elementDefaultValue } from '../../utils/elementDefaults';
import { isHiddenFromMe, isSeeOnlyElement } from '../../utils/elementAccess';
import { normalizeId } from '../../utils/ids';

export const LEAVE_EMPTY = '';
export const COMMENT = 'comment';
export const LOSE = 'lose';

const hasOwn = (obj, key) => !!obj && Object.prototype.hasOwnProperty.call(obj, key);

/**
 * The selection the page was opened with, read from its search params:
 * `card` (the source card) and exactly one of `ids` (comma-separated), `q`
 * (a list filter) or `all=1`. Anything else is null — the page says it cannot
 * tell which items to change rather than guessing.
 */
export function selectionFromSearch(params) {
	const get = key => (params && typeof params.get === 'function' ? params.get(key) : null);
	const card = normalizeId(get('card'));
	if (!card) return null;
	const ids = (get('ids') || '').split(',').map(id => id.trim()).filter(Boolean);
	const q = (get('q') || '').trim();
	const all = get('all') === '1' || get('all') === 'true';
	const given = [ids.length > 0, !!q, all].filter(Boolean).length;
	if (given !== 1) return null;
	if (ids.length) return { card, ids: [...new Set(ids)] };
	if (q) return { card, q };
	return { card, all: true };
}

/** The element names the caller cannot see, and can see but not change. */
export function accessNames(elements = []) {
	const hidden = [];
	const seeOnly = [];
	(elements || []).forEach(el => {
		if (!el?.name) return;
		if (isHiddenFromMe(el)) hidden.push(el.name);
		else if (isSeeOnlyElement(el)) seeOnly.push(el.name);
	});
	return { hidden, seeOnly };
}

/** The source card's elements a person may map: never one hidden from them. */
export function sourcesOf(elements = []) {
	return offeredSources(elements, { hidden: accessNames(elements).hidden });
}

/**
 * The target card's elements: `offered` may be filled; `withheld` are listed
 * with the sentence saying why not (keepr writes it, the person can only see
 * it, a repeating group). A hidden one is in neither.
 */
export function targetsOf(elements = []) {
	return offeredTargets(elements, accessNames(elements));
}

/**
 * `ctx.reaches(targetCardId, sourceCardId)` for the table: an item lookup
 * pointing at the first card accepts items of the second when the second IS
 * the first or sits beneath it.
 */
export function createReaches(cardsById = {}) {
	return (targetCardId, sourceCardId) => {
		const to = normalizeId(targetCardId);
		if (!to) return false;
		return cardChain(cardsById, sourceCardId).some(card => normalizeId(card?._id) === to);
	};
}

/** The table's answer for one pair. */
export function verdictFor(source, target, ctx) {
	return classify(source, target, ctx);
}

/** May this source be dropped on that target? `{ ok, reason }`. */
export function dropVerdict(source, target, ctx) {
	const answer = classify(source, target, ctx);
	return answer.verdict === 'refused' ? { ok: false, reason: answer.reason } : { ok: true, reason: null, answer };
}

/**
 * What the pair asks for once, preset the way the table says: a choice map
 * started by value then label, the unit the target (or source) defaults to,
 * the currency the target defaults to (else the collection's) or the
 * source's. A choice the suggestion cannot place is left out of the map, for
 * the person to map or leave empty — the dry run counts it until they do.
 */
export function defaultMappingFor(source, target, answer, { collectionCurrency = null } = {}) {
	const out = {};
	(answer?.needs || []).forEach(need => {
		if (need.kind === 'choices') out.choices = suggestChoiceMap(source, target);
		else if (need.kind === 'unit') {
			const unit = defaultUnitFor((need.of === 'source' ? source : target)?.options || {});
			if (unit) out.unit = unit;
		} else if (need.kind === 'currency') {
			const options = (need.of === 'source' ? source : target)?.options || {};
			const code = defaultCodeOf(options) || (need.of === 'target' ? collectionCurrency : null);
			if (code) out.currency = code;
		}
	});
	return out;
}

/**
 * Fill a target with a source, with the pair's preset mapping. A drop on a
 * text that already has a fill EXTENDS it (`opts.extend`): the pill is added
 * after what is there, in the composer; any other drop, and the picker,
 * replace.
 */
export function assign(mapping, targetName, source, target, ctx, opts) {
	const answer = classify(source, target, ctx);
	if (answer.verdict === 'refused') return mapping;
	if (opts && opts.extend && hasOwn(mapping, targetName) && takesParts(target)) {
		const text = `${entryText(mapping[targetName])}${tokenOf({ base: source.name })}`;
		return { ...mapping, [targetName]: { kind: COMPOSE, text } };
	}
	return { ...mapping, [targetName]: { source: source.name, ...defaultMappingFor(source, target, answer, opts) } };
}

export const COMPOSE = 'compose';

/** Is this entry a value built in the composer? */
export const isCompose = entry => !!entry && entry.kind === COMPOSE;

/** The composer's text for an entry: its own, or the one pill an element entry stands for. */
export function entryText(entry) {
	if (isCompose(entry)) return typeof entry.text === 'string' ? entry.text : '';
	return entry && entry.source ? tokenOf({ base: entry.source }) : '';
}

const namesOf = sourceNames => (sourceNames instanceof Set ? sourceNames : new Set(sourceNames || []));

/** The source element an entry is exactly, as the picker and the compatibility table read it: or null. */
export function plainSourceName(entry, sourceNames) {
	if (!entry) return null;
	if (!isCompose(entry)) return entry.source || null;
	return plainSourceOf(entry.text, namesOf(sourceNames));
}

/** The source elements an entry reads (a pill, a transform, a dot-walk's first element). */
export function entryReads(entry, sourceNames) {
	if (!entry) return [];
	if (!isCompose(entry)) return entry.source ? [entry.source] : [];
	return sourcesRead(entry.text, namesOf(sourceNames));
}

/** A composer with nothing in it yet fills nothing. */
export const isBlankCompose = entry => isCompose(entry) && !String(entry.text || '').trim();

/** The mapping without the composers that hold nothing: what the dry run, the suggestions and the review see. */
export function activeMapping(mapping = {}) {
	if (!Object.keys(mapping).some(name => isBlankCompose(mapping[name]))) return mapping;
	const out = {};
	Object.keys(mapping).forEach(name => { if (!isBlankCompose(mapping[name])) out[name] = mapping[name]; });
	return out;
}

/**
 * The fill an entry stands for on its target: `{ fill }`, `{ empty: true }`
 * (nothing to send) or `{ problem }` (one sentence; nothing is sent).
 */
export function entryFill(entry, target, sourceNames) {
	if (!isCompose(entry)) return { fill: fillOf(entry) };
	if (!target) return { empty: true };
	return composerFill(entry.text, target, { sourceNames: namesOf(sourceNames), mapping: entry });
}

/** The sentence each composer that cannot be sent says, by target: `{ [targetName]: sentence }`. */
export function composeProblems(mapping = {}, targetsByName = {}, sourceNames = []) {
	const out = {};
	Object.keys(mapping).forEach(name => {
		if (!isCompose(mapping[name]) || !targetsByName[name]) return;
		const made = entryFill(mapping[name], targetsByName[name], sourceNames);
		if (made.problem) out[name] = made.problem;
	});
	return out;
}

/** Open the composer on a target: from the element it is filled with, or empty. */
export function buildValue(mapping, targetName) {
	const entry = mapping[targetName];
	if (isCompose(entry)) return mapping;
	if (!entry) return { ...mapping, [targetName]: { kind: COMPOSE, text: '' } };
	const carried = {};
	['choices', 'unit', 'currency'].forEach(key => { if (entry[key] !== undefined) carried[key] = entry[key]; });
	return { ...mapping, [targetName]: { kind: COMPOSE, text: entryText(entry), ...carried } };
}

/**
 * The composer's text changed. When it is still exactly one plain pill of the
 * same source, what the pair needs (choices, unit, currency) stays; when the
 * pill changed it starts again from the pair's presets; when it is no longer
 * one plain pill, they go.
 */
export function composeText(mapping, targetName, text, { sourcesByName = {}, targetsByName = {}, ctx = null, opts = {} } = {}) {
	const entry = mapping[targetName];
	if (!hasOwn(mapping, targetName)) return mapping;
	const names = Object.keys(sourcesByName);
	const before = plainSourceName(entry, names);
	const after = plainSourceOf(text, new Set(names));
	let carried = {};
	if (after && after === before) {
		['choices', 'unit', 'currency'].forEach(key => { if (entry[key] !== undefined) carried[key] = entry[key]; });
	} else if (after && sourcesByName[after] && targetsByName[targetName]) {
		const answer = classify(sourcesByName[after], targetsByName[targetName], ctx);
		if (answer.verdict !== 'refused') carried = defaultMappingFor(sourcesByName[after], targetsByName[targetName], answer, opts);
	}
	return { ...mapping, [targetName]: { kind: COMPOSE, text, ...carried } };
}

/** A composer that is exactly one plain pill goes back to the picker's element fill. */
export function toElementEntry(mapping, targetName, sourceNames) {
	const entry = mapping[targetName];
	if (!isCompose(entry)) return mapping;
	const source = plainSourceOf(entry.text, namesOf(sourceNames));
	if (!source) return mapping;
	const next = { source };
	['choices', 'unit', 'currency'].forEach(key => { if (entry[key] !== undefined) next[key] = entry[key]; });
	return { ...mapping, [targetName]: next };
}

export function clear(mapping, targetName) {
	if (!hasOwn(mapping, targetName)) return mapping;
	const next = { ...mapping };
	delete next[targetName];
	return next;
}

/** Change one setting of a target's mapping (`choices`, `unit`, `currency`). */
export function updateMapping(mapping, targetName, patch) {
	if (!hasOwn(mapping, targetName)) return mapping;
	return { ...mapping, [targetName]: { ...mapping[targetName], ...patch } };
}

const fold = text => String(text ?? '').trim().toLowerCase();
const labelOf = el => {
	const label = el?.label;
	const text = label && typeof label === 'object' ? label.singular : label;
	return typeof text === 'string' && text.trim() ? text.trim() : el?.name || '';
};

/** The person's word for an element: its label, else its name. */
export const elementLabel = labelOf;

/**
 * Same-name suggestions for the targets nothing fills yet: a source whose
 * name, or label, is the target's (ignoring case and surrounding spaces) and
 * which the table lets fill it. Nothing is mapped until the person accepts.
 */
export function suggestionsFor(sources = [], targets = [], mapping = {}, ctx) {
	const out = [];
	targets.forEach(target => {
		if (hasOwn(mapping, target.name)) return;
		const match = sources.find(source => source.name === target.name)
			|| sources.find(source => fold(labelOf(source)) && fold(labelOf(source)) === fold(labelOf(target)));
		if (!match) return;
		if (classify(match, target, ctx).verdict === 'refused') return;
		out.push({ target: target.name, source: match.name });
	});
	return out;
}

/** Accept suggestions (all of them, or the ones given) into the mapping. */
export function acceptSuggestions(mapping, suggestions, sourcesByName, targetsByName, ctx, opts) {
	return suggestions.reduce((next, { target, source }) => (
		assign(next, target, sourcesByName[source], targetsByName[target], ctx, opts)
	), mapping);
}

/** A fill as the dry run reads it: `{ kind: 'element', element, mapping? }`. */
export function fillOf(entry) {
	const mapping = {};
	if (entry.choices) mapping.choices = entry.choices;
	if (entry.unit) mapping.unit = entry.unit;
	if (entry.currency) mapping.currency = entry.currency;
	return Object.keys(mapping).length
		? { kind: 'element', element: entry.source, mapping }
		: { kind: 'element', element: entry.source };
}

/**
 * The dry run's body. A target left out starts at its own default, as a new
 * item's does. Only a `lose` is sent for data left behind: a comment is the
 * default, and a choice for an element that is no longer left behind would be
 * refused (`unmapped`).
 *
 * The review step (KPR-709) adds `asks` — `{ [targetName]: value }`, one
 * value for every item of a required target nothing maps, sent as an `ask`
 * fill — and `rules`, a manager's ticks `{ [ruleId]: boolean }`.
 */
export function dryRunBody(selection, targetCardId, mapping = {}, leftBehind = {}, sourceNames = null, { asks = {}, rules = {}, targetsByName = null } = {}) {
	if (!selection || !targetCardId) return null;
	const body = { card: selection.card, target: targetCardId };
	if (selection.ids) body.ids = selection.ids;
	else if (selection.q) body.q = selection.q;
	else body.all = true;
	body.fills = {};
	const names = sourceNames || Object.values(mapping).map(entry => entry.source).filter(Boolean);
	Object.keys(mapping).forEach(name => {
		const entry = mapping[name];
		// A value built in the composer is sent as the fill it stands for; one
		// that stands for nothing yet, or that the composer refuses (its row
		// says why, and the review waits), is left out.
		if (isCompose(entry)) {
			const made = entryFill(entry, targetsByName && targetsByName[name], names);
			if (made.fill) body.fills[name] = made.fill;
			return;
		}
		body.fills[name] = fillOf(entry);
	});
	Object.keys(asks || {}).forEach(name => {
		if (hasOwn(body.fills, name) || hasOwn(mapping, name) || asks[name] === undefined) return;
		body.fills[name] = { kind: 'ask', value: asks[name] };
	});
	if (rules && Object.keys(rules).length) body.rules = { ...rules };
	const mapped = new Set();
	Object.values(mapping).forEach(entry => entryReads(entry, names).forEach(name => mapped.add(name)));
	const unmapped = {};
	Object.keys(leftBehind).forEach(name => {
		if (leftBehind[name] !== LOSE || mapped.has(name)) return;
		if (sourceNames && !sourceNames.includes(name)) return;
		unmapped[name] = LOSE;
	});
	if (Object.keys(unmapped).length) body.unmapped = unmapped;
	return body;
}

/**
 * The card a change's signature is taken for: the target's when it asks for
 * one on every change, else the source's (keepr-api apply.js reads it the
 * same way). The most specific explicit record setting of a card's chain
 * wins, as `cardIntakeRefusal` reads it.
 */
export function signingCardId(cardsById = {}, targetCardId, sourceCardId) {
	const asks = id => {
		const specific = [...cardChain(cardsById, id)].reverse().find(card => card?.options?.record !== undefined && card?.options?.record !== null);
		const record = specific?.options?.record;
		return !!(record && record.attest);
	};
	return asks(targetCardId) ? normalizeId(targetCardId) : normalizeId(sourceCardId);
}

/** The source elements a mapping reads, each with the targets it fills. */
export function goesTo(mapping = {}, sourceNames = null) {
	const out = {};
	Object.keys(mapping).forEach(target => {
		entryReads(mapping[target], sourceNames || [mapping[target].source]).forEach(source => {
			(out[source] = out[source] || []).push(target);
		});
	});
	return out;
}

/**
 * A target row's status: `filled` (a source fills it), `default` (nothing
 * does and it starts at its own default), `required` (nothing does, it has no
 * default, and it must be filled), `problem` (the dry run refused its fill) or
 * `empty`. `dry` is the dry run's entry for it, when there is one.
 */
export function targetStatus(target, entry, dry, problem) {
	// "Required" is a problem the dry run names too; the row keeps saying so.
	const problems = Array.isArray(problem) ? problem : (problem ? [problem] : []);
	if (!entry && problems.length && problems.every(p => p?.code === 'required')) return 'required';
	if (problems.length) return 'problem';
	if (entry) return 'filled';
	if (dry?.fill === 'default') return 'default';
	const required = dry ? dry.required === true : target?.options?.required === true;
	if (required) return 'required';
	if (!dry && hasDefault(target)) return 'default';
	return 'empty';
}

// Before the first dry run answers: what a new item would start at.
const hasDefault = target => elementDefaultValue(target) !== undefined;

/** The dry run's problems keyed by target element (several may name one). */
export function problemsByTarget(problems = []) {
	const out = {};
	(problems || []).forEach(problem => {
		if (!problem?.element) return;
		(out[problem.element] = out[problem.element] || []).push(problem);
	});
	return out;
}

/**
 * The one line under the target picker, from the dry run's `items`:
 * "3 of 4 items will move. 1 will not. 2 are skipped."
 */
export function summaryLine(items) {
	if (!items) return null;
	const evaluated = Number(items.evaluated) || 0;
	const moving = Number(items.moving) || 0;
	const failing = Number(items.failing) || 0;
	const skipped = Object.values(items.skipped || {}).reduce((sum, n) => sum + (Number(n) || 0), 0);
	const parts = [];
	if (!evaluated) parts.push('No items can move.');
	else if (moving === evaluated) parts.push(evaluated === 1 ? 'The item will move.' : `All ${evaluated} items will move.`);
	else parts.push(`${moving} of ${evaluated} item${evaluated === 1 ? '' : 's'} will move.`);
	if (evaluated && failing) parts.push(`${failing} will not.`);
	if (skipped) parts.push(`${skipped} ${skipped === 1 ? 'is' : 'are'} skipped.`);
	return parts.join(' ');
}
